import React from 'react';
import { Link, useParams } from 'react-router-dom';
import './PrivacyPolicy.css';

// 通用隐私政策内容（保持你现在的内容不变）
const GenericPrivacyContent = () => (
  <>
    <section className="privacy-section">
      <h2>1. Introduction</h2>
      <p>
        Xi'an Jiutian Chuangxiang Network Technology Co., Ltd. ("we," "our," or "us") is committed to protecting your privacy. 
        This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website 
        or use our mobile games and services.
      </p>
    </section>

    <section className="privacy-section">
      <h2>2. Information We Collect</h2>
      <h3>2.1 Personal Information</h3>
      <p>We may collect personal information that you voluntarily provide to us, including:</p>
      <ul>
        <li>Name and contact information</li>
        <li>Email address</li>
        <li>Device information and identifiers</li>
        <li>Game progress and achievements</li>
      </ul>

      <h3>2.2 Automatically Collected Information</h3>
      <p>When you use our games or visit our website, we may automatically collect:</p>
      <ul>
        <li>IP address and location data</li>
        <li>Device type, operating system, and browser information</li>
        <li>Game usage statistics and performance data</li>
        <li>Advertising identifiers</li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2>3. How We Use Your Information</h2>
      <p>We use the collected information for the following purposes:</p>
      <ul>
        <li>To provide and maintain our games and services</li>
        <li>To improve game performance and user experience</li>
        <li>To personalize content and advertisements</li>
        <li>To communicate with users about updates and support</li>
        <li>To analyze usage patterns and optimize our services</li>
        <li>To comply with legal obligations</li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2>4. Information Sharing and Disclosure</h2>
      <p>We may share your information in the following circumstances:</p>
      <ul>
        <li><strong>Third-Party Service Providers:</strong> We may share data with trusted partners who help us operate our services</li>
        <li><strong>Advertising Partners:</strong> We may share advertising identifiers with ad networks to show relevant ads</li>
        <li><strong>Legal Requirements:</strong> We may disclose information when required by law or to protect our rights</li>
        <li><strong>Business Transfers:</strong> Information may be transferred in connection with mergers or acquisitions</li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2>5. Data Security</h2>
      <p>
        We implement appropriate technical and organizational security measures to protect your personal information 
        against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over 
        the internet or electronic storage is 100% secure.
      </p>
    </section>

    <section className="privacy-section">
      <h2>6. Children's Privacy</h2>
      <p>
        Our games may be suitable for children, but we do not knowingly collect personal information from children 
        under 13 years of age without parental consent. If you are a parent or guardian and believe we have collected 
        information from your child, please contact us immediately.
      </p>
    </section>

    <section className="privacy-section">
      <h2>7. International Data Transfers</h2>
      <p>
        Your information may be transferred to and processed in countries other than your country of residence. 
        We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards.
      </p>
    </section>

    <section className="privacy-section">
      <h2>8. Your Rights</h2>
      <p>Depending on your location, you may have the following rights regarding your personal information:</p>
      <ul>
        <li>Right to access your personal data</li>
        <li>Right to rectify inaccurate information</li>
        <li>Right to erase your personal data</li>
        <li>Right to restrict processing</li>
        <li>Right to data portability</li>
        <li>Right to object to processing</li>
      </ul>
    </section>

    <section className="privacy-section">
      <h2>9. Cookies and Tracking Technologies</h2>
      <p>
        We use cookies and similar tracking technologies to enhance your experience on our website and in our games. 
        You can control cookie settings through your browser preferences.
      </p>
    </section>

    <section className="privacy-section">
      <h2>10. Updates to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of any material changes by posting 
        the new Privacy Policy on this page and updating the "Last updated" date.
      </p>
    </section>

    <section className="privacy-section">
      <h2>11. Contact Us</h2>
      <p>If you have any questions about this Privacy Policy, please contact us:</p>
      <p>
        Xi'an Jiutian Chuangxiang Network Technology Co., Ltd.<br />
        Room 22801, Building 2, No. 91, Jinye Road, Zhangba Sub-district, High-tech Zone,<br />
        Xi'an, Shaanxi Province, China<br />
        Email: support@jiutiantech.com
      </p>
    </section>
  </>
);

// App privacy policy content with Firebase, Adjust, AdMob mediation, and permissions disclosure.
const AdMobMediationPrivacyContent = ({
    appName = 'Color Box - Sort Puzzle',
    packageName = 'com.jiutiantech.boxsort',
}) => {
    const dynamicReceiverPermission = `${packageName}.DYNAMIC_RECEIVER_NOT_EXPORTED_PERMISSION`;

    return (
        <>
        <section className="privacy-section">
            <h2>Introduction</h2>
            <p>
                Xi'an Jiutian Chuangxiang Network Technology Co., Ltd. (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) operates the mobile game
                <strong> {appName}</strong> (the &quot;App&quot;).
            </p>
            <p>
                This Privacy Policy explains how we collect, use, and protect information when you install and use the App. By using
                the App, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not
                agree, please do not install or use the App.
            </p>
            <p>
                We respect the privacy of our users and are committed to complying with applicable data protection laws, including
                the General Data Protection Regulation (GDPR) and the California Consumer Privacy Act (CCPA) where applicable.
            </p>
            <p>
                The App uses third-party analytics, attribution, and advertising services such as Firebase, Adjust, and Google AdMob
                (with mediation partners including Meta Audience Network, AppLovin, Pangle, Unity Ads, IronSource, Liftoff, and
                Mintegral) to help us understand how the App is used and to display ads.
            </p>
        </section>

        <section className="privacy-section">
            <h2>1. Data Controller and Contact Details</h2>
            <p>For the purposes of data protection laws (including the GDPR), the data controller is:</p>
            <p>
                Xi'an Jiutian Chuangxiang Network Technology Co., Ltd.<br />
                Room 22801, Building 2, No. 91, Jinye Road, Zhangba Sub-district, High-tech Zone,<br />
                Xi'an, Shaanxi Province, China<br />
                Email: support@jiutiantech.com
            </p>
            <p>
                We have not appointed a separate Data Protection Officer. If you have any questions about this Privacy Policy or our
                data practices, you can contact us at <strong>support@jiutiantech.com</strong>.
            </p>
        </section>

        <section className="privacy-section">
            <h2>2. What Information We Collect</h2>
            <p>
                We do not create or maintain our own player accounts or player database. The App itself does not require you to
                register or provide your name, email address, or other direct personal identifiers to play.
            </p>
            <p>
                However, the App integrates third-party services including Firebase (provided by Google), Adjust, and Google AdMob
                (and its mediation partners) that may collect information from your device for analytics, attribution, crash
                reporting, remote configuration, advertising, and monetization purposes.
            </p>

            <h3>2.1 Information Collected Automatically (via SDKs and advertising partners)</h3>
            <p>
                When you use the App, certain information may be collected automatically by third-party SDKs such as Google Mobile
                Ads SDK (AdMob), Adjust SDK, Firebase SDK, and by AdMob&apos;s mediation partners (including Meta Audience Network,
                AppLovin, Pangle, Unity Ads, IronSource, Liftoff, and Mintegral). This may include:
            </p>

            <h4>Device and technical information</h4>
            <ul>
                <li>Device model and manufacturer</li>
                <li>Operating system and version</li>
                <li>Language and region settings</li>
                <li>App version and installation source (e.g. Google Play)</li>
            </ul>

            <h4>Identifiers</h4>
            <ul>
                <li>Android Advertising ID, app set ID, or other advertising identifiers (where available)</li>
                <li>Device identifiers generated by the operating system, Firebase, Adjust, or the advertising SDKs</li>
                <li>IP address (used, among other things, to derive approximate location such as country or region)</li>
            </ul>

            <h4>Usage and analytics data</h4>
            <ul>
                <li>Screens you view and features you use in the App</li>
                <li>Session length, frequency of use, and general interaction with the App</li>
                <li>
                    Events related to gameplay (for example, level progress, feature usage) in an aggregated, analytics-oriented form
                </li>
            </ul>

            <h4>Advertising and attribution data</h4>
            <ul>
                <li>
                    Information about ads shown in the App (for example, which ad was shown, how often it was shown, and where it appeared
                    in the game)
                </li>
                <li>
                    Information about your interaction with ads (for example, whether an ad was viewed, clicked, or completed, and whether
                    a reward was granted)
                </li>
                <li>
                    Information used for attribution and campaign performance measurement (for example, install source, campaign identifiers,
                    and in-app events related to ads or purchases)
                </li>
                <li>
                    Information used for fraud detection and prevention (for example, signals used to detect invalid traffic or abuse)
                </li>
            </ul>

            <h4>Crash and error information</h4>
            <p>
                Information about app crashes and errors (through Firebase Crashlytics and similar tools), including device information,
                OS version, the part of the App where the error occurred, and timestamps.
            </p>
            <p>
                We use these services mainly to understand how the App is used, improve performance and stability, show and optimize
                ads, and fix technical issues.
            </p>

            <h3>2.2 Information You Provide to Us</h3>
            <p>
                If you contact us (for example, by email) for support or feedback, we may receive:
            </p>
            <ul>
                <li>Your email address and/or display name</li>
                <li>
                    The content of your message and any information you voluntarily provide (for example, screenshots, logs, or
                    device information)
                </li>
            </ul>
            <p>We use this information only to respond to your request and improve our services.</p>
        </section>

        <section className="privacy-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the information collected, mainly through Firebase, Adjust, AdMob, and their related SDKs, for the following purposes:</p>

            <h3>To provide and operate the App</h3>
            <ul>
                <li>Ensure the App functions correctly on your device</li>
                <li>Maintain and improve the core gameplay experience</li>
            </ul>

            <h3>To analyze and improve the App</h3>
            <ul>
                <li>Monitor performance, stability, and usage patterns</li>
                <li>Understand how players interact with the App so we can fix bugs, improve levels, and optimize user experience</li>
                <li>Use remote configuration (Firebase Remote Config) to adjust certain in-app parameters without requiring a new app update</li>
            </ul>

            <h3>To handle crashes and errors</h3>
            <ul>
                <li>Diagnose technical issues and crashes</li>
                <li>Improve the stability and reliability of the App</li>
            </ul>

            <h3>For advertising and monetization</h3>
            <ul>
                <li>Display ads within the App using Google AdMob and its mediation partners</li>
                <li>Decide which ads to show, including limiting how often you see the same ad</li>
                <li>Measure the performance and revenue of ads (for example, impressions, clicks, completed views, rewards, and in-app events)</li>
                <li>Detect and prevent fraud, invalid traffic, and abuse of advertising systems</li>
            </ul>

            <h3>For security and legal compliance</h3>
            <ul>
                <li>Protect against fraud, misuse, and unauthorized access</li>
                <li>Comply with legal obligations and respond to lawful requests from authorities, where required</li>
            </ul>

            <p>
                The App displays third-party ads provided via Google AdMob and its mediation partners. Depending on your location,
                device settings, and any consent choices you make, these ads may be personalized (based on your activity and device
                information) or non-personalized. In some regions, you may see a consent or privacy message that allows you to manage
                your advertising preferences.
            </p>
        </section>

        <section className="privacy-section">
            <h2>4. Legal Bases for Processing (EEA/UK Users)</h2>
            <p>
                If you are located in the European Economic Area (EEA) or the United Kingdom, we process your personal data on the
                following legal bases:
            </p>

            <h3>Legitimate interests</h3>
            <p>
                We process information (such as analytics, attribution, advertising, and crash data) because it is in our legitimate
                interest to:
            </p>
            <ul>
                <li>Ensure the security and proper functioning of the App</li>
                <li>Understand usage to improve and develop the App</li>
                <li>Show and measure ads in order to fund the App and keep it available</li>
                <li>Fix bugs and improve stability</li>
            </ul>

            <h3>Consent</h3>
            <p>
                In some cases, your device, operating system, or our in-app consent tools may ask for your consent before certain data is
                collected or before certain identifiers are used (for example, for personalized ads in certain regions). Where your
                consent is required by law, we will rely on that consent as the legal basis for processing. You may withdraw your consent
                at any time, for example by changing your device privacy settings or your choices in any consent message displayed in the App.
            </p>

            <h3>Legal obligations</h3>
            <p>
                We may also process or retain certain information where we are legally required to do so (for example for accounting,
                tax, or regulatory purposes).
            </p>
        </section>

        <section className="privacy-section">
            <h2>5. Third-Party Services (Firebase, Adjust, AdMob, and others)</h2>
            <p>The App integrates the following third-party services:</p>
            <ul>
                <li>Firebase Analytics – for app usage analytics</li>
                <li>Firebase Crashlytics – for crash reporting and stability diagnostics</li>
                <li>Firebase Remote Config – to remotely manage certain app configuration values</li>
                <li>
                    Adjust – for install and event attribution, campaign performance measurement, and related analytics and fraud prevention
                </li>
                <li>
                    Google AdMob and Google Mobile Ads SDK – to display ads in the App and to measure, optimize, and report on ad
                    performance and revenue
                </li>
            </ul>
            <p>
                Through AdMob&apos;s mediation feature, ads in the App may also be served by third-party advertising networks
                (mediation partners), including but not limited to Meta Audience Network, AppLovin, Pangle, Unity Ads, IronSource,
                Liftoff, and Mintegral. These partners may collect or receive the types of information described in Section 2 (such as
                device identifiers, IP address, approximate location, and interaction with ads) in order to provide, personalize, and
                measure ads, as well as to detect and prevent fraud.
            </p>
            <p>
                These services are provided and operated by their respective companies. When you use the App, these services may
                collect and process data as described in their own privacy policies.
            </p>
            <p>For more information about how our key providers handle your data, please refer to:</p>
            <ul>
                <li>
                    Google Privacy Policy:{" "}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                        https://policies.google.com/privacy
                    </a>
                </li>
                <li>
                    Google Advertising technologies:{" "}
                    <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noreferrer">
                        https://policies.google.com/technologies/ads
                    </a>
                </li>
                <li>
                    Firebase Privacy and Security:{" "}
                    <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer">
                        https://firebase.google.com/support/privacy
                    </a>
                </li>
                <li>
                    Google Mobile Ads SDK data disclosure (AdMob):{" "}
                    <a
                        href="https://developers.google.com/admob/android/privacy/play-data-disclosure"
                        target="_blank"
                        rel="noreferrer"
                    >
                        https://developers.google.com/admob/android/privacy/play-data-disclosure
                    </a>
                </li>
                <li>
                    Adjust Privacy Policy:{" "}
                    <a href="https://www.adjust.com/terms/privacy-policy/" target="_blank" rel="noreferrer">
                        https://www.adjust.com/terms/privacy-policy/
                    </a>
                </li>
            </ul>

            <h3>Advertising networks and their partners</h3>
            <p>
                The following companies assist us by providing advertising and marketing services. If you reside in the European
                Economic Area (EEA) or other regions where consent is required, these advertising networks, analytics providers, and
                other listed companies may request your consent to collect or receive device information from our App for advertising
                purposes.
            </p>
            <p>
                This may include personal and non-personal data such as advertising identifiers (e.g., Android Advertising ID), IP
                address, information about ad delivery and interactions, and other tracking technologies used to provide measurement
                services, personalization, ad selection, delivery, reporting, and targeted ad optimization.
            </p>
            <p>
                In most cases, the advertising networks listed below act as independent data controllers in relation to your personal
                data. You can learn more about how each provider handles your data or exercise opt-out choices by reviewing their
                respective privacy policies:
            </p>
            <ul>
                <li>
                    AppLovin (AppLovin Corporation):{" "}
                    <a href="https://legal.applovin.com/privacy/" target="_blank" rel="noreferrer">
                        https://legal.applovin.com/privacy/
                    </a>
                </li>
                <li>
                    Facebook Audience Network (Meta Platforms, Inc.):{" "}
                    <a href="https://www.facebook.com/privacy/policy/" target="_blank" rel="noreferrer">
                        https://www.facebook.com/privacy/policy/
                    </a>
                </li>
                <li>
                    Google AdMob and their partners (Google Inc.):{" "}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                        https://policies.google.com/privacy
                    </a>
                </li>
                <li>
                    IronSource (Unity Software Inc.):{" "}
                    <a
                        href="https://developers.ironsrc.com/ironsource-mobile/air/ironsource-mobile-privacy-policy/"
                        target="_blank"
                        rel="noreferrer"
                    >
                        https://developers.ironsrc.com/ironsource-mobile/air/ironsource-mobile-privacy-policy/
                    </a>
                </li>
                <li>
                    Liftoff:{" "}
                    <a href="https://liftoff.io/privacy-policy/" target="_blank" rel="noreferrer">
                        https://liftoff.io/privacy-policy/
                    </a>
                </li>
                <li>
                    Mintegral:{" "}
                    <a href="https://www.mintegral.com/en/privacy/" target="_blank" rel="noreferrer">
                        https://www.mintegral.com/en/privacy/
                    </a>
                </li>
                <li>
                    Pangle:{" "}
                    <a href="https://www.pangleglobal.com/privacy/enduser-en" target="_blank" rel="noreferrer">
                        https://www.pangleglobal.com/privacy/enduser-en
                    </a>
                </li>
                <li>
                    Unity Ads (Unity Technologies):{" "}
                    <a href="https://unity.com/legal/privacy-policy" target="_blank" rel="noreferrer">
                        https://unity.com/legal/privacy-policy
                    </a>
                </li>
            </ul>
            <p>
                For more information about how to manage or opt out of personalized advertising, please refer to the privacy policies
                listed above or your device&apos;s ad settings.
            </p>
        </section>

        <section className="privacy-section">
            <h2>6. Permissions Used by the App</h2>
            <p>
                To provide core functionality and a smooth gaming and advertising experience, the App requests certain permissions
                from your device. Depending on your device model and Android version, the following permissions may be used:
            </p>

            <h3>6.1 android.permission.INTERNET</h3>
            <p>This permission allows the App to access the internet. It is used to:</p>
            <ul>
                <li>Send analytics, attribution, and crash reports via Firebase, Adjust, and similar services</li>
                <li>Fetch remote configuration values via Firebase Remote Config</li>
                <li>Request and display ads via Google AdMob and its mediation partners</li>
            </ul>
            <p>
                Without internet access, some online features (such as analytics-based improvements, remote configuration updates,
                ad loading, or error reporting) may not function properly. We do not use this permission to read your personal files or content.
            </p>

            <h3>6.2 android.permission.ACCESS_NETWORK_STATE</h3>
            <p>This permission allows the App to check the status of your network connection (for example, Wi-Fi or mobile data).</p>
            <p>It is used to:</p>
            <ul>
                <li>Determine whether it is appropriate to send analytics, attribution, and crash data</li>
                <li>Avoid repeated connection attempts when the device is offline</li>
                <li>Decide when to request and refresh ads</li>
            </ul>
            <p>This permission does not provide access to the content of your communications.</p>

            <h3>6.3 android.permission.WAKE_LOCK</h3>
            <p>
                This permission allows the App to request that the device stay awake (screen not dim or lock) while you are actively
                playing, in order to:
            </p>
            <ul>
                <li>Prevent interruptions during gameplay</li>
                <li>Ensure a smoother user experience</li>
            </ul>
            <p>
                We do not use this permission to run hidden background processes unrelated to the App&apos;s normal operation.
            </p>

            <h3>6.4 android.permission.VIBRATE</h3>
            <p>
                This permission enables optional haptic feedback (device vibration) during gameplay, such as when certain in-game
                actions occur. It is used purely for user experience and does not provide access to any personal information.
            </p>

            <h3>6.5 com.google.android.finsky.permission.BIND_GET_INSTALL_REFERRER_SERVICE</h3>
            <p>
                This permission is used by the Google Play Install Referrer API. It allows the App (through Google Play services,
                Firebase, Adjust, and advertising SDKs such as AdMob) to obtain non-personal information about how the app install
                occurred, for example:
            </p>
            <ul>
                <li>Whether the install came directly from the Google Play Store search or from a specific campaign</li>
                <li>Basic attribution information used for analytics, advertising measurement, and user acquisition analysis</li>
            </ul>
            <p>
                This helps us understand how users discover the App and improve our user acquisition and advertising strategies. It
                does not allow the App to install other applications on your device, and it does not give us access to your personal files.
            </p>

            <h3>6.6 com.google.android.gms.permission.AD_ID</h3>
            <p>
                This permission allows Google Play services, Firebase, Adjust, and Google Mobile Ads SDK (AdMob) to access the
                Advertising ID provided by Google Play services on the device. In our App, this identifier may be used:
            </p>
            <ul>
                <li>For analytics, measurement, and attribution within Firebase and Adjust (for example, to understand which campaigns led to installs)</li>
                <li>
                    For advertising, including showing, personalizing (where permitted by law and your choices), capping, and measuring
                    ads served via AdMob and its mediation partners
                </li>
                <li>
                    To help Google and our partners improve their own services in a privacy-preserving way, according to their policies
                </li>
            </ul>
            <p>
                We do not link this ID with direct identifiers such as your name or email. You may reset or limit the use of the
                Advertising ID in your device&apos;s Google settings, and in some regions you may also choose to receive only
                non-personalized ads.
            </p>
            <p>
                On supported Android versions, you can also manage or delete your Advertising ID and related ad personalization
                settings in your device&apos;s system settings.
            </p>

            <h3>6.7 android.permission.ACCESS_ADSERVICES_ATTRIBUTION</h3>
            <p>
                This permission relates to Android&apos;s ad services Attribution Reporting APIs within the AdServices framework.
                It may allow Google Play services, Google AdMob, and Adjust to access privacy-preserving attribution signals for
                installs and in-app events.
            </p>
            <p>
                These signals are designed to help measure which advertising campaigns or traffic sources led to an install or
                in-app event, while limiting the sharing of user-identifying information. In our App, this functionality is accessed
                only through third-party SDKs (such as Google Mobile Ads SDK / AdMob and Adjust). We do not directly read or store
                raw attribution data from these APIs ourselves.
            </p>
            <p>
                Use of the Attribution Reporting APIs is governed by Google&apos;s own privacy policies and your Android privacy
                settings. On supported Android versions, you may find related controls in your device&apos;s Privacy Sandbox or ad settings.
            </p>

            <h3>6.8 android.permission.ACCESS_ADSERVICES_AD_ID</h3>
            <p>
                This permission relates to Android&apos;s AdServices advertising ID within the same Privacy Sandbox framework.
                It may allow Google Play services, Google AdMob, and Adjust to use a privacy-preserving advertising identifier on
                supported Android versions.
            </p>
            <p>
                In our App, this identifier may be used by advertising and analytics SDKs to help show, cap, and measure ads, and
                to understand the performance of advertising campaigns, in a way that is more privacy-friendly than traditional
                device identifiers. We do not link this identifier to direct identifiers such as your name or email.
            </p>
            <p>
                Use of the AdServices advertising ID is governed by Google&apos;s privacy policies and your Android privacy and ad
                settings. On supported devices, you can review or change these settings in the system settings (for example, under
                Privacy Sandbox or Ads).
            </p>

            <h3>6.9 android.permission.ACCESS_ADSERVICES_TOPICS</h3>
            <p>
                This permission relates to Android&apos;s Privacy Sandbox Topics API. It may allow Google Play services, Google AdMob,
                and certain advertising partners to access privacy-preserving interest topics that are generated on your device based
                on your recent app usage.
            </p>
            <p>
                These topics can be used to help show more relevant ads without sharing your full browsing or app history with
                advertisers. In our App, we access this functionality only through advertising SDKs (such as Google Mobile Ads SDK /
                AdMob). We do not directly read or store your detailed topics ourselves.
            </p>
            <p>
                Use of the Topics API is governed by Google&apos;s own privacy policies and your Android Privacy Sandbox settings.
                On supported Android versions, you can manage or disable ad topics in your device&apos;s Privacy Sandbox or ad settings.
            </p>

            <h3>6.10 android.permission.FOREGROUND_SERVICE</h3>
            <p>
                This permission allows the App or certain system components (such as Android&apos;s WorkManager or Google Play services)
                to run foreground services when necessary. Foreground services are used for tasks that need to continue running even
                if the App is not actively on screen, and may require showing a small system notification.
            </p>
            <p>
                In our App, this capability may be used by third-party SDKs (such as Firebase or Google Play services) for background
                tasks like scheduling analytics uploads, crash reporting, or other maintenance operations. We do not use this permission
                to access your location, microphone, camera, or personal files.
            </p>

            <h3>6.11 {dynamicReceiverPermission}</h3>
            <p>
                This is a custom application-specific permission used internally by the App and its components. It is defined with a
                protection level that restricts its use to trusted components and is not available to unrelated third-party apps.
            </p>
            <p>
                This permission is mainly used to ensure that internal broadcast receivers or components can communicate securely inside
                the App and does not grant access to your personal data to external parties.
            </p>

            <p>
                We only request the permissions necessary to provide the App&apos;s core functionality, advertising support, and improve
                stability and user experience. We do not use these permissions to access your personal files (such as photos, contacts,
                or messages), and we do not use them to directly identify you as an individual.
            </p>
            <p>
                The App does not request location permissions (such as access to GPS or precise location) and does not access your
                precise location from the device. However, third-party services integrated into the App (such as Firebase, Adjust,
                and AdMob) may use your IP address to derive an approximate location (for example, country or region) for analytics,
                attribution, and advertising reporting. This process does not involve precise location tracking from your device&apos;s
                GPS or similar sensors.
            </p>
            <p>
                Where supported by your device, you may review and manage App permissions at any time in your device&apos;s system settings.
            </p>
        </section>

        <section className="privacy-section">
            <h2>7. Log Data and Cookies</h2>

            <h3>7.1 Log Data</h3>
            <p>
                In the event of an error or crash in the App, we may collect data and information (through Firebase Crashlytics and
                related tools) called &quot;Log Data&quot;. This may include:
            </p>
            <ul>
                <li>Device IP address (at the time of the crash)</li>
                <li>Device name and model</li>
                <li>Operating system version</li>
                <li>App configuration when using the App</li>
                <li>The time and date of the crash and other statistics</li>
            </ul>
            <p>This information helps us diagnose and fix technical issues.</p>

            <h3>7.2 Cookies and Similar Technologies</h3>
            <p>
                The App itself does not use traditional browser cookies. However, third-party services integrated into the App, such as
                Firebase, Adjust, Google AdMob, and their partners, may use or access identifiers and technologies that serve a similar
                purpose to cookies (for example, device identifiers, local storage, or similar tracking technologies) for analytics,
                attribution, advertising, and crash reporting.
            </p>
            <p>
                You can control or limit the collection of certain information by adjusting your device&apos;s privacy settings, Google
                account settings, and (where available) your choices in any consent or privacy messages shown in the App.
            </p>
        </section>

        <section className="privacy-section">
            <h2>8. International Data Transfers</h2>
            <p>
                Your information may be processed by us or by our service providers (such as Google/Firebase, Adjust, and advertising
                partners) in countries other than the one in which you reside. These countries may have data protection laws that are
                different from those in your country and may not be as protective.
            </p>
            <p>
                Where required by law (for example for users in the EEA/UK), appropriate safeguards such as EU Standard Contractual
                Clauses or equivalent mechanisms are used to protect your personal data during international transfers.
            </p>
        </section>

        <section className="privacy-section">
            <h2>9. Data Retention and Deletion</h2>
            <p>
                We retain data only for as long as necessary to achieve the purposes described in this Privacy Policy, including:
            </p>
            <ul>
                <li>Providing and maintaining the App</li>
                <li>Improving performance and user experience</li>
                <li>Displaying, measuring, and improving advertising</li>
                <li>Complying with our legal obligations</li>
                <li>Resolving disputes and enforcing our agreements</li>
            </ul>
            <p>
                When data is no longer needed, we will delete it, anonymize it, or aggregate it so that it no longer identifies you.
            </p>
            <p>
                If you would like us to request deletion or anonymization of data associated with your use of the App, you may contact
                us at <strong>support@jiutiantech.com</strong>. We may ask you for certain information (for example, details about your
                device or usage) to help us locate the relevant data.
            </p>
            <p>Please note that:</p>
            <ul>
                <li>Some data may be retained for a longer period if required by law.</li>
                <li>
                    Data collected and processed by Firebase, Adjust, AdMob, and other partners is also subject to their privacy policies;
                    for some requests, you may need to contact them directly or manage your data via your Google account or other
                    provider-specific tools.
                </li>
            </ul>
        </section>

        <section className="privacy-section">
            <h2>10. Your Rights</h2>

            <h3>10.1 Rights for EEA/UK Users (GDPR)</h3>
            <p>
                If you are located in the EEA or UK, you have certain rights regarding your personal data, subject to legal limitations:
            </p>
            <ul>
                <li>
                    <strong>Right of access</strong> – You can request confirmation of whether we process your personal data and request
                    a copy of that data.
                </li>
                <li>
                    <strong>Right to rectification</strong> – You can request correction of inaccurate or incomplete data.
                </li>
                <li>
                    <strong>Right to erasure (&quot;right to be forgotten&quot;)</strong> – You can request deletion of your personal data
                    in certain circumstances.
                </li>
                <li>
                    <strong>Right to restriction of processing</strong> – You can request that we restrict the processing of your data
                    under certain conditions.
                </li>
                <li>
                    <strong>Right to data portability</strong> – You can request to receive your data in a structured, commonly used,
                    machine-readable format and have it transmitted to another controller where technically feasible.
                </li>
                <li>
                    <strong>Right to object</strong> – You can object to processing based on our legitimate interests, including
                    profiling, and to processing for direct marketing.
                </li>
                <li>
                    <strong>Right to withdraw consent</strong> – Where processing is based on your consent, you may withdraw that consent
                    at any time.
                </li>
            </ul>
            <p>
                To exercise your rights, please contact us at <strong>support@jiutiantech.com</strong>. We may request additional
                information to verify your identity. You also have the right to lodge a complaint with your local data protection
                authority.
            </p>

            <h3>10.2 Rights for California Residents (CCPA)</h3>
            <p>
                If you are a California resident, you may have the following rights under the CCPA and its amendments, subject to
                certain exceptions:
            </p>
            <ul>
                <li>
                    <strong>Right to know</strong> – Request information about what personal information we have collected about you and
                    how it is used and shared.
                </li>
                <li>
                    <strong>Right to delete</strong> – Request deletion of personal information that we have collected from you, subject
                    to certain legal exceptions.
                </li>
                <li>
                    <strong>Right to correct</strong> – Request correction of inaccurate personal information.
                </li>
                <li>
                    <strong>Right to non-discrimination</strong> – We will not discriminate against you for exercising your privacy
                    rights.
                </li>
            </ul>
            <p>
                We do not sell your personal information for money. However, we allow our advertising partners (such as Google AdMob and
                its mediation partners) to collect or receive certain information from the App (for example, advertising identifiers,
                IP address, and information about your interactions with ads) to show and measure ads. Depending on your jurisdiction,
                this may be considered &quot;sharing&quot; or &quot;targeted advertising&quot; under applicable privacy laws.
                Where required, we will provide you with additional notices or controls to manage such sharing.
            </p>
            <p>
                To exercise your CCPA rights, please contact us at <strong>support@jiutiantech.com</strong> and specify that your
                request relates to the &quot;CCPA&quot;.
            </p>
        </section>

        <section className="privacy-section">
            <h2>11. Children&apos;s Privacy</h2>
            <p>
                The App is not directed to children under the age of 13, and we do not knowingly collect personal information from
                children under 13 (or a higher age threshold where required by local law).
            </p>
            <p>
                If we become aware that we have collected personal information from a child without verifiable parental consent, we
                will take steps to delete such information as soon as reasonably possible and, if necessary, restrict that user&apos;s
                access to the App.
            </p>
            <p>
                If you are a parent or guardian and believe that your child has provided us with personal information, please contact
                us at <strong>support@jiutiantech.com</strong>.
            </p>
        </section>

        <section className="privacy-section">
            <h2>12. Security</h2>
            <p>
                We take reasonable technical and organizational measures to help protect information processed through the App against
                unauthorized access, loss, misuse, or alteration.
            </p>
            <p>
                However, no method of transmission over the internet or method of electronic storage is completely secure. While we
                strive to use commercially acceptable means to protect information, we cannot guarantee absolute security.
            </p>
            <p>
                If we become aware of a data breach that is likely to result in a high risk to your rights and freedoms, we will
                notify you and the relevant authorities in accordance with applicable laws.
            </p>
        </section>

        <section className="privacy-section">
            <h2>13. Changes to This Privacy Policy</h2>
            <p>
                We may update this Privacy Policy from time to time, for example to reflect:
            </p>
            <ul>
                <li>Changes in the App or our data practices</li>
                <li>Changes required by applicable laws or regulations</li>
            </ul>
            <p>
                When we make material changes, we will update the &quot;Last updated&quot; date at the top of this Privacy Policy
                and, where appropriate, notify you within the App or by other reasonable means.
            </p>
            <p>
                Your continued use of the App after the updated Privacy Policy becomes effective will signify your acceptance of the
                changes. If you do not agree to the updated policy, you should stop using the App and uninstall it.
            </p>
        </section>

        <section className="privacy-section">
            <h2>14. Contact Us</h2>
            <p>
                If you have any questions, comments, or concerns about this Privacy Policy or our data practices, please contact us at:
            </p>
            <p>
                Xi'an Jiutian Chuangxiang Network Technology Co., Ltd.<br />
                Room 22801, Building 2, No. 91, Jinye Road, Zhangba Sub-district, High-tech Zone,<br />
                Xi'an, Shaanxi Province, China<br />
                Email: support@jiutiantech.com
            </p>
        </section>
        </>
    );
};

const BoxSortPrivacyContent = () => (
    <AdMobMediationPrivacyContent />
);

// Firebase-only app privacy policy content based on the 2025-11-13 Box Sort policy.
const FirebaseOnlyPrivacyContent = ({ appName, packageName }) => {
    const dynamicReceiverPermission = `${packageName}.DYNAMIC_RECEIVER_NOT_EXPORTED_PERMISSION`;

    return (
        <>
            <section className="privacy-section">
                <h2>Introduction</h2>
                <p>
                    Xi'an Jiutian Chuangxiang Network Technology Co., Ltd. (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) operates the mobile game
                    <strong> {appName}</strong> (the &quot;App&quot;).
                </p>
                <p>
                    This Privacy Policy explains how we collect, use, and protect information when you install and use the App. By using
                    the App, you agree to the collection and use of information in accordance with this Privacy Policy. If you do not
                    agree, please do not install or use the App.
                </p>
                <p>
                    We respect the privacy of our users and are committed to complying with applicable data protection laws, including
                    the General Data Protection Regulation (GDPR) and the California Consumer Privacy Act (CCPA) where applicable.
                </p>
            </section>

            <section className="privacy-section">
                <h2>1. Data Controller and Contact Details</h2>
                <p>For the purposes of data protection laws (including the GDPR), the data controller is:</p>
                <p>
                    Xi'an Jiutian Chuangxiang Network Technology Co., Ltd.<br />
                    Room 22801, Building 2, No. 91, Jinye Road, Zhangba Sub-district, High-tech Zone,<br />
                    Xi'an, Shaanxi Province, China<br />
                    Email: support@jiutiantech.com
                </p>
                <p>
                    We have not appointed a separate Data Protection Officer. If you have any questions about this Privacy Policy or our
                    data practices, you can contact us at <strong>support@jiutiantech.com</strong>.
                </p>
            </section>

            <section className="privacy-section">
                <h2>2. What Information We Collect</h2>
                <p>
                    We do not create or maintain our own player accounts or player database. The App itself does not require you to
                    register or provide your name, email address, or other direct personal identifiers to play.
                </p>
                <p>
                    However, the App integrates third-party services (mainly Firebase, provided by Google) that may collect information
                    from your device for analytics, crash reporting, and remote configuration.
                </p>

                <h3>2.1 Information Collected Automatically (via SDKs)</h3>
                <p>
                    When you use the App, certain information may be collected automatically by our third-party service provider
                    Firebase, operated by Google. This may include:
                </p>
                <h4>Device and technical information</h4>
                <ul>
                    <li>Device model and manufacturer</li>
                    <li>Operating system and version</li>
                    <li>Language and region settings</li>
                    <li>App version and installation source (e.g. Google Play)</li>
                </ul>
                <h4>Identifiers</h4>
                <ul>
                    <li>Android Advertising ID or other advertising identifiers (where available)</li>
                    <li>Device identifiers generated by the operating system or Firebase</li>
                    <li>IP address (used, among other things, to derive approximate location such as country or region)</li>
                </ul>
                <h4>Usage and analytics data</h4>
                <ul>
                    <li>Screens you view and features you use in the App</li>
                    <li>Session length, frequency of use, and general interaction with the App</li>
                    <li>
                        Events related to gameplay (for example, level progress, feature usage) in an aggregated, analytics-oriented form
                    </li>
                </ul>
                <h4>Crash and error information</h4>
                <p>
                    Information about app crashes and errors (through Firebase Crashlytics), including device information, OS version,
                    the part of the App where the error occurred, and timestamps.
                </p>
                <p>
                    We use these services mainly to understand how the App is used, improve performance and stability, and fix technical
                    issues.
                </p>

                <h3>2.2 Information You Provide to Us</h3>
                <p>
                    If you contact us (for example, by email) for support or feedback, we may receive:
                </p>
                <ul>
                    <li>Your email address and/or display name</li>
                    <li>
                        The content of your message and any information you voluntarily provide (for example, screenshots, logs, or
                        device information)
                    </li>
                </ul>
                <p>We use this information only to respond to your request and improve our services.</p>
            </section>

            <section className="privacy-section">
                <h2>3. How We Use Your Information</h2>
                <p>We use the information collected, mainly through Firebase, for the following purposes:</p>
                <h3>To provide and operate the App</h3>
                <ul>
                    <li>Ensure the App functions correctly on your device</li>
                    <li>Maintain and improve the core gameplay experience</li>
                </ul>
                <h3>To analyze and improve the App</h3>
                <ul>
                    <li>Monitor performance, stability, and usage patterns</li>
                    <li>Understand how players interact with the App so we can fix bugs, improve levels, and optimize user experience</li>
                    <li>Use remote configuration (Firebase Remote Config) to adjust certain in-app parameters without requiring a new app update</li>
                </ul>
                <h3>To handle crashes and errors</h3>
                <ul>
                    <li>Diagnose technical issues and crashes</li>
                    <li>Improve the stability and reliability of the App</li>
                </ul>
                <h3>For security and legal compliance</h3>
                <ul>
                    <li>Protect against fraud, misuse, and unauthorized access</li>
                    <li>Comply with legal obligations and respond to lawful requests from authorities, where required</li>
                </ul>
                <p>
                    We do not currently display third-party ads in the App and do not use your data for advertising or ad personalization.
                </p>
            </section>

            <section className="privacy-section">
                <h2>4. Legal Bases for Processing (EEA/UK Users)</h2>
                <p>
                    If you are located in the European Economic Area (EEA) or the United Kingdom, we process your personal data on the
                    following legal bases:
                </p>
                <h3>Legitimate interests</h3>
                <p>
                    We process information (such as analytics and crash data) because it is in our legitimate interest to:
                </p>
                <ul>
                    <li>Ensure the security and proper functioning of the App</li>
                    <li>Understand usage to improve and develop the App</li>
                    <li>Fix bugs and improve stability</li>
                </ul>
                <h3>Consent</h3>
                <p>
                    In some cases, your device or operating system may ask for your consent before certain data is collected or before
                    certain identifiers are used. Where your consent is required by law, we will rely on that consent as the legal basis
                    for processing. You may withdraw your consent at any time, for example by changing your device privacy settings.
                </p>
                <h3>Legal obligations</h3>
                <p>
                    We may also process or retain certain information where we are legally required to do so (for example for accounting,
                    tax, or regulatory purposes).
                </p>
            </section>

            <section className="privacy-section">
                <h2>5. Third-Party Services (Firebase / Google)</h2>
                <p>The App integrates the following third-party services provided by Google:</p>
                <ul>
                    <li>Firebase Analytics - for app usage analytics</li>
                    <li>Firebase Crashlytics - for crash reporting and stability diagnostics</li>
                    <li>Firebase Remote Config - to remotely manage certain app configuration values</li>
                </ul>
                <p>
                    These services are provided and operated by Google LLC and/or its affiliates. When you use the App, these services may
                    collect and process data as described in their own privacy policies.
                </p>
                <p>For more information about how Google and Firebase handle your data, please refer to:</p>
                <ul>
                    <li>
                        Google Privacy Policy:{" "}
                        <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
                            https://policies.google.com/privacy
                        </a>
                    </li>
                    <li>
                        Firebase Privacy and Security:{" "}
                        <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noreferrer">
                            https://firebase.google.com/support/privacy
                        </a>
                    </li>
                </ul>
                <p>
                    Google may process your information on servers located in various countries. Where required, Google implements
                    appropriate safeguards for international data transfers (such as standard contractual clauses).
                </p>
                <p>
                    We do not currently integrate any third-party advertising SDKs, and the App does not currently display third-party ads.
                </p>
            </section>

            <section className="privacy-section">
                <h2>6. Permissions Used by the App</h2>
                <p>
                    To provide core functionality and a smooth gaming experience, the App requests certain permissions from your device.
                    Depending on your device model and Android version, the following permissions may be used:
                </p>

                <h3>6.1 android.permission.INTERNET</h3>
                <p>This permission allows the App to access the internet. It is used to:</p>
                <ul>
                    <li>Send analytics events and crash reports via Firebase Analytics and Firebase Crashlytics</li>
                    <li>Fetch remote configuration values via Firebase Remote Config</li>
                </ul>
                <p>
                    Without internet access, some online features (such as analytics-based improvements, remote configuration updates, or
                    error reporting) may not function properly. We do not use this permission to read your personal files or content.
                </p>

                <h3>6.2 android.permission.ACCESS_NETWORK_STATE</h3>
                <p>This permission allows the App to check the status of your network connection (for example, Wi-Fi or mobile data).</p>
                <p>It is used to:</p>
                <ul>
                    <li>Determine whether it is appropriate to send analytics and crash data</li>
                    <li>Avoid repeated connection attempts when the device is offline</li>
                </ul>
                <p>This permission does not provide access to the content of your communications.</p>

                <h3>6.3 android.permission.WAKE_LOCK</h3>
                <p>
                    This permission allows the App to request that the device stay awake (screen not dim or lock) while you are actively
                    playing, in order to:
                </p>
                <ul>
                    <li>Prevent interruptions during gameplay</li>
                    <li>Ensure a smoother user experience</li>
                </ul>
                <p>
                    We do not use this permission to run hidden background processes unrelated to the App&apos;s normal operation.
                </p>

                <h3>6.4 android.permission.VIBRATE</h3>
                <p>
                    This permission enables optional haptic feedback (device vibration) during gameplay, such as when certain in-game
                    actions occur. It is used purely for user experience and does not provide access to any personal information.
                </p>

                <h3>6.5 com.google.android.finsky.permission.BIND_GET_INSTALL_REFERRER_SERVICE</h3>
                <p>
                    This permission is used by the Google Play Install Referrer API. It allows the App (through Google Play Services and
                    Firebase) to obtain non-personal information about how the app install occurred, for example:
                </p>
                <ul>
                    <li>Whether the install came directly from the Google Play Store search or from a specific campaign</li>
                    <li>Basic attribution information used for analytics (not for advertising in this App)</li>
                </ul>
                <p>
                    This helps us understand how users discover the App and improve our user acquisition strategies. No direct personally
                    identifiable information (such as your name or email) is collected through this mechanism.
                </p>

                <h3>6.6 com.google.android.gms.permission.AD_ID</h3>
                <p>
                    This permission allows Google Play Services and Firebase to access the Advertising ID provided by Google Play services
                    on the device. In our App, this identifier may be used:
                </p>
                <ul>
                    <li>For analytics, measurement and attribution within Firebase</li>
                    <li>
                        To help Google improve its own services in a privacy-preserving way, according to Google&apos;s policies
                    </li>
                </ul>
                <p>
                    We do not currently display third-party ads inside the App and do not use the Advertising ID for ad targeting in this
                    App. We do not link this ID with direct identifiers such as your name or email.
                </p>
                <p>You may reset or limit the use of the Advertising ID in your device&apos;s Google settings.</p>

                <h3>6.7 android.permission.ACCESS_ADSERVICES_ATTRIBUTION</h3>
                <h3>6.8 android.permission.ACCESS_ADSERVICES_AD_ID</h3>
                <p>
                    These permissions relate to Android&apos;s Privacy Sandbox ad services APIs (such as Attribution Reporting and Ad ID within
                    the AdServices framework). They may allow Google Play services and Firebase to:
                </p>
                <ul>
                    <li>Access privacy-preserving attribution signals</li>
                    <li>Use a privacy-preserving advertising identifier in supported Android versions</li>
                </ul>
                <p>
                    In our App, these APIs are used only through Google/Firebase SDKs and only for analytics and attribution purposes,
                    not for showing third-party ads. The App itself does not directly access precise ad topics or personal profiles.
                </p>
                <p>
                    Use of these APIs is governed by Google&apos;s own privacy policies and your Android privacy settings. You can manage related
                    settings in your device&apos;s system settings when available.
                </p>

                <h3>6.9 {dynamicReceiverPermission}</h3>
                <p>
                    This is a custom application-specific permission used internally by the App and its components. It is defined with a
                    protection level that restricts its use to trusted components and is not available to unrelated third-party apps.
                </p>
                <p>
                    This permission is mainly used to ensure that internal broadcast receivers or components can communicate securely inside
                    the App and does not grant access to your personal data to external parties.
                </p>

                <p>
                    We only request the permissions necessary to provide the App&apos;s core functionality and improve stability and user
                    experience. We do not use these permissions to access your personal files (such as photos, contacts, or messages), and
                    we do not use them to directly identify you as an individual.
                </p>
                <p>
                    Where supported by your device, you may review and manage App permissions at any time in your device&apos;s system settings.
                </p>
            </section>

            <section className="privacy-section">
                <h2>7. Log Data and Cookies</h2>

                <h3>7.1 Log Data</h3>
                <p>
                    In the event of an error or crash in the App, we may collect data and information (through Firebase Crashlytics) called
                    &quot;Log Data&quot;. This may include:
                </p>
                <ul>
                    <li>Device IP address (at the time of the crash)</li>
                    <li>Device name and model</li>
                    <li>Operating system version</li>
                    <li>App configuration when using the App</li>
                    <li>The time and date of the crash and other statistics</li>
                </ul>
                <p>This information helps us diagnose and fix technical issues.</p>

                <h3>7.2 Cookies and Similar Technologies</h3>
                <p>
                    The App itself does not use traditional browser cookies. However, third-party services integrated into the App, such as
                    Firebase, may use or access identifiers and technologies that serve a similar purpose to cookies (for example, device
                    identifiers or local storage) for analytics and crash reporting.
                </p>
                <p>
                    You can control or limit the collection of certain information by adjusting your device&apos;s privacy settings or Google
                    account settings.
                </p>
            </section>

            <section className="privacy-section">
                <h2>8. International Data Transfers</h2>
                <p>
                    Your information may be processed by us or by our service providers (such as Google/Firebase) in countries other than
                    the one in which you reside. These countries may have data protection laws that are different from those in your
                    country and may not be as protective.
                </p>
                <p>
                    Where required by law (for example for users in the EEA/UK), appropriate safeguards such as EU Standard Contractual
                    Clauses or equivalent mechanisms are used to protect your personal data during international transfers.
                </p>
            </section>

            <section className="privacy-section">
                <h2>9. Data Retention and Deletion</h2>
                <p>
                    We retain data only for as long as necessary to achieve the purposes described in this Privacy Policy, including:
                </p>
                <ul>
                    <li>Providing and maintaining the App</li>
                    <li>Improving performance and user experience</li>
                    <li>Complying with our legal obligations</li>
                    <li>Resolving disputes and enforcing our agreements</li>
                </ul>
                <p>
                    When data is no longer needed, we will delete it, anonymize it, or aggregate it so that it no longer identifies you.
                </p>
                <p>
                    If you would like us to request deletion or anonymization of data associated with your use of the App, you may contact
                    us at <strong>support@jiutiantech.com</strong>. We may ask you for certain information (for example, details about your
                    device or usage) to help us locate the relevant data.
                </p>
                <p>Please note that:</p>
                <ul>
                    <li>Some data may be retained for a longer period if required by law.</li>
                    <li>
                        Data collected and processed by Firebase is also subject to Google&apos;s privacy policies; for some requests, you may
                        need to contact Google directly or manage your data via your Google account.
                    </li>
                </ul>
            </section>

            <section className="privacy-section">
                <h2>10. Your Rights</h2>

                <h3>10.1 Rights for EEA/UK Users (GDPR)</h3>
                <p>
                    If you are located in the EEA or UK, you have certain rights regarding your personal data, subject to legal limitations:
                </p>
                <ul>
                    <li>
                        <strong>Right of access</strong> - You can request confirmation of whether we process your personal data and request
                        a copy of that data.
                    </li>
                    <li>
                        <strong>Right to rectification</strong> - You can request correction of inaccurate or incomplete data.
                    </li>
                    <li>
                        <strong>Right to erasure (&quot;right to be forgotten&quot;)</strong> - You can request deletion of your personal data
                        in certain circumstances.
                    </li>
                    <li>
                        <strong>Right to restriction of processing</strong> - You can request that we restrict the processing of your data
                        under certain conditions.
                    </li>
                    <li>
                        <strong>Right to data portability</strong> - You can request to receive your data in a structured, commonly used,
                        machine-readable format and have it transmitted to another controller where technically feasible.
                    </li>
                    <li>
                        <strong>Right to object</strong> - You can object to processing based on our legitimate interests, including
                        profiling, and to processing for direct marketing (we currently do not use your data for marketing).
                    </li>
                    <li>
                        <strong>Right to withdraw consent</strong> - Where processing is based on your consent, you may withdraw that consent
                        at any time.
                    </li>
                </ul>
                <p>
                    To exercise your rights, please contact us at <strong>support@jiutiantech.com</strong>. We may request additional
                    information to verify your identity. You also have the right to lodge a complaint with your local data protection
                    authority.
                </p>

                <h3>10.2 Rights for California Residents (CCPA)</h3>
                <p>
                    If you are a California resident, you may have the following rights under the CCPA and its amendments, subject to
                    certain exceptions:
                </p>
                <ul>
                    <li>
                        <strong>Right to know</strong> - Request information about what personal information we have collected about you and
                        how it is used and shared.
                    </li>
                    <li>
                        <strong>Right to delete</strong> - Request deletion of personal information that we have collected from you, subject
                        to certain legal exceptions.
                    </li>
                    <li>
                        <strong>Right to correct</strong> - Request correction of inaccurate personal information.
                    </li>
                    <li>
                        <strong>Right to non-discrimination</strong> - We will not discriminate against you for exercising your privacy
                        rights.
                    </li>
                </ul>
                <p>
                    We do not sell your personal information and do not share it for cross-context behavioral advertising as those terms
                    are defined under the CCPA.
                </p>
                <p>
                    To exercise your CCPA rights, please contact us at <strong>support@jiutiantech.com</strong> and specify that your
                    request relates to the &quot;CCPA&quot;.
                </p>
            </section>

            <section className="privacy-section">
                <h2>11. Children&apos;s Privacy</h2>
                <p>
                    The App is not directed to children under the age of 13, and we do not knowingly collect personal information from
                    children under 13 (or a higher age threshold where required by local law).
                </p>
                <p>
                    If we become aware that we have collected personal information from a child without verifiable parental consent, we
                    will take steps to delete such information as soon as reasonably possible and, if necessary, restrict that user&apos;s
                    access to the App.
                </p>
                <p>
                    If you are a parent or guardian and believe that your child has provided us with personal information, please contact
                    us at <strong>support@jiutiantech.com</strong>.
                </p>
            </section>

            <section className="privacy-section">
                <h2>12. Security</h2>
                <p>
                    We take reasonable technical and organizational measures to help protect information processed through the App against
                    unauthorized access, loss, misuse, or alteration.
                </p>
                <p>
                    However, no method of transmission over the internet or method of electronic storage is completely secure. While we
                    strive to use commercially acceptable means to protect information, we cannot guarantee absolute security.
                </p>
                <p>
                    If we become aware of a data breach that is likely to result in a high risk to your rights and freedoms, we will
                    notify you and the relevant authorities in accordance with applicable laws.
                </p>
            </section>

            <section className="privacy-section">
                <h2>13. Changes to This Privacy Policy</h2>
                <p>
                    We may update this Privacy Policy from time to time, for example to reflect:
                </p>
                <ul>
                    <li>Changes in the App or our data practices</li>
                    <li>Changes required by applicable laws or regulations</li>
                </ul>
                <p>
                    When we make material changes, we will update the &quot;Last updated&quot; date at the top of this Privacy Policy
                    and, where appropriate, notify you within the App or by other reasonable means.
                </p>
                <p>
                    Your continued use of the App after the updated Privacy Policy becomes effective will signify your acceptance of the
                    changes. If you do not agree to the updated policy, you should stop using the App and uninstall it.
                </p>
            </section>

            <section className="privacy-section">
                <h2>14. Contact Us</h2>
                <p>
                    If you have any questions, comments, or concerns about this Privacy Policy or our data practices, please contact us at:
                </p>
                <p>
                    Xi'an Jiutian Chuangxiang Network Technology Co., Ltd.<br />
                    Room 22801, Building 2, No. 91, Jinye Road, Zhangba Sub-district, High-tech Zone,<br />
                    Xi'an, Shaanxi Province, China<br />
                    Email: support@jiutiantech.com
                </p>
            </section>
        </>
    );
};

const BoltBloomPrivacyContent = () => (
    <AdMobMediationPrivacyContent
        appName="BoltBloom: Nut Sort Puzzle"
        packageName="com.jiutiantech.boltbloom.nutsortpuzzle"
    />
);

const privacyPolicies = {
  boxsort: {
    title: 'Privacy Policy: Color Box - Sort Puzzle',
    lastUpdated: '2025-12-09',
    Content: BoxSortPrivacyContent,
  },
  boltbloom: {
    title: 'Privacy Policy: BoltBloom: Nut Sort Puzzle',
    lastUpdated: '2026-05-07',
    Content: BoltBloomPrivacyContent,
  },
};

const PrivacyPolicy = () => {
  const { appId } = useParams();
  const policy = privacyPolicies[appId] || {
    title: 'Privacy Policy',
    lastUpdated: '2025-09-01',
    Content: GenericPrivacyContent,
  };
  const Content = policy.Content;

  return (
    <div className="privacy-policy">
      <div className="privacy-header">
        <div className="container">
          <Link to="/" className="back-link">← Back to Home</Link>
          <h1>{policy.title}</h1>
          <p className="last-updated">Last updated: {policy.lastUpdated}</p>
        </div>
      </div>

      <div className="privacy-content">
        <div className="container">
          <div className="content-wrapper">
            <Content />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
